import { bestTextOn, contrastRatio, ensureContrast, isHexColor, mix } from "./color";

export type ResolvedTheme = "light" | "dark";

/** Backgrounds the accent is checked against; keep in step with tokens.css. */
const SURFACES = {
  light: { surface: "#ffffff", nav: "#0f1b33" },
  dark: { surface: "#131c31", nav: "#080e1c" },
} as const;

export const MIN_TEXT_CONTRAST = 4.5;

export interface BrandTokens {
  accent: string;
  accentHover: string;
  onAccent: string;
  accentText: string;
  accentSoft: string;
  navAccent: string;
}

/**
 * Turns a club's brand color into the accent tokens, nudging lightness wherever the color
 * alone would not be readable:
 *  - text on the accent (buttons) is white or near-black, whichever reads better, and the accent
 *    itself is darkened/lightened until that text reaches 4.5:1 (WCAG AA);
 *  - the accent used as link/icon text reaches 4.5:1 on the surface and on its own tinted background;
 *  - the sidebar marker reaches 3:1 on the navy sidebar.
 * Returns null when the value is not a #rrggbb color, so callers fall back to the product accent.
 */
export function deriveBrandTokens(color: string | null | undefined, theme: ResolvedTheme): BrandTokens | null {
  if (!isHexColor(color)) return null;
  const base = `#${color.trim().replace("#", "").toLowerCase()}`;
  const { surface, nav } = SURFACES[theme];

  let onAccent = bestTextOn(base);
  let accent = base;
  if (contrastRatio(onAccent, accent) < MIN_TEXT_CONTRAST) {
    // Mid-tones cannot carry either text color: push the fill toward the side that helps.
    accent = ensureContrast(base, onAccent, MIN_TEXT_CONTRAST, onAccent === "#ffffff" ? "darker" : "lighter");
    onAccent = bestTextOn(accent);
  }
  const accentHover = mix(accent, onAccent === "#ffffff" ? "#000000" : "#ffffff", 0.14);
  const softBase = mix(surface, accent, theme === "light" ? 0.12 : 0.2);
  let accentText = ensureContrast(accent, surface, MIN_TEXT_CONTRAST, theme === "light" ? "darker" : "lighter");
  accentText = ensureContrast(accentText, softBase, MIN_TEXT_CONTRAST, theme === "light" ? "darker" : "lighter");
  const navAccent = ensureContrast(accent, nav, 3, "lighter");

  return { accent, accentHover, onAccent, accentText, accentSoft: softBase, navAccent };
}

const PROPERTIES: Record<keyof BrandTokens, string> = {
  accent: "--cc-accent",
  accentHover: "--cc-accent-hover",
  onAccent: "--cc-on-accent",
  accentText: "--cc-accent-text",
  accentSoft: "--cc-accent-soft",
  navAccent: "--cc-nav-accent",
};

/** Writes the tokens as inline custom properties on `root`, or removes them to restore the stylesheet defaults. */
export function applyBrandTokens(root: HTMLElement, tokens: BrandTokens | null): void {
  for (const [key, property] of Object.entries(PROPERTIES)) {
    if (tokens) root.style.setProperty(property, tokens[key as keyof BrandTokens]);
    else root.style.removeProperty(property);
  }
}
